<template>
  <v-card-text class="card-content">
    <v-list dense class="model-list">
      <v-list-item
        v-for="model in displayedModelle"
        :key="model.id"
        @dblclick="editModel(model)"
        class="model-item"
        rounded
      >
        <template v-slot:prepend>
          <v-icon icon="mdi-coffee-maker" class="me-2" size="small" color="green-lighten-1"></v-icon>
        </template>
        
        <v-list-item-title>
          {{ model.modell }}
          <span v-if="model.tassenzahl" class="model-cups">
            ({{ model.tassenzahl }})
          </span>
        </v-list-item-title>
        
        <div class="model-details mt-1">
          <!-- Kaffee-Temperatur Informationen -->
          <v-chip
            v-if="model.kaffee_temp_min || model.kaffee_temp_max || model.kaffee_temp_soll"
            class="temp-chip ma-1"
            size="x-small"
            variant="outlined"
            color="light-blue-accent-1"
            prepend-icon="mdi-coffee"
          >
            <span v-if="model.kaffee_temp_soll">
              Kaffee: {{ model.kaffee_temp_soll }}°
            </span>
            <span v-else-if="model.kaffee_temp_min && model.kaffee_temp_max">
              Kaffee: {{ model.kaffee_temp_min }}° - {{ model.kaffee_temp_max }}°
            </span>
            <span v-else-if="model.kaffee_temp_min">
              Kaffee min: {{ model.kaffee_temp_min }}°
            </span>
            <span v-else-if="model.kaffee_temp_max">
              Kaffee max: {{ model.kaffee_temp_max }}°
            </span>
          </v-chip>

          <!-- Milch-Temperatur Informationen -->
          <v-chip
            v-if="model.milch_temp_min || model.milch_temp_max || model.milch_temp_soll"
            class="temp-chip ma-1"
            size="x-small"
            variant="outlined"
            color="indigo-lighten-2"
            prepend-icon="mdi-cup-water"
          >
            <span v-if="model.milch_temp_soll">
              Milch: {{ model.milch_temp_soll }}°
            </span>
            <span v-else-if="model.milch_temp_min && model.milch_temp_max">
              Milch: {{ model.milch_temp_min }}° - {{ model.milch_temp_max }}°
            </span>
            <span v-else-if="model.milch_temp_min">
              Milch min: {{ model.milch_temp_min }}°
            </span>
            <span v-else-if="model.milch_temp_max">
              Milch max: {{ model.milch_temp_max }}°
            </span>
          </v-chip>

          <!-- Alte Temperatur-Informationen (für Kompatibilität) -->
          <v-chip
            v-if="(model.temp_min || model.temp_max) && !model.kaffee_temp_min && !model.kaffee_temp_max && !model.milch_temp_min && !model.milch_temp_max"
            class="temp-chip ma-1"
            size="x-small"
            variant="outlined"
            color="light-blue-accent-1"
            prepend-icon="mdi-thermometer"
          >
            <span v-if="model.temp_min && model.temp_max">
              {{ model.temp_min }}° - {{ model.temp_max }}°
            </span>
            <span v-else-if="model.temp_min">
              Min: {{ model.temp_min }}°
            </span>
            <span v-else-if="model.temp_max">
              Max: {{ model.temp_max }}°
            </span>
          </v-chip>
          
          <!-- Weitere Info-Badges -->
          <v-chip
            v-if="model.ws_menue"
            class="feature-chip ma-1"
            size="x-small"
            variant="flat"
            color="green-lighten-3"
          >
            WS: {{ truncateText(model.ws_menue, 20) }}
          </v-chip>
          
          <v-chip
            v-if="model.kalibrieren"
            class="feature-chip ma-1"
            size="x-small"
            variant="flat"
            color="amber-lighten-3"
          >
            Kal: {{ truncateText(model.kalibrieren, 20) }}
          </v-chip>
        </div>

        <v-list-item-subtitle v-if="model.sonstiges" class="mt-1">
          {{ truncateText(model.sonstiges, 60) }}
        </v-list-item-subtitle>
      </v-list-item>

      <v-list-item v-if="globalModelle.length === 0" disabled>
        <v-list-item-title class="text-center text-grey">
          Keine Modelle vorhanden
        </v-list-item-title>
      </v-list-item>
    </v-list>

    <div class="d-flex justify-end mt-2" v-if="globalModelle.length > 4">
      <v-btn
        text
        size="small"
        variant="text"
        color="success"
        class="show-more-button"
        @click="toggleShowAllModelle"
      >
        <v-icon size="small" class="mr-1">
          {{ showAllModelle ? "mdi-chevron-up" : "mdi-chevron-down" }}
        </v-icon>
        {{ showAllModelle ? "Weniger anzeigen" : "Alle anzeigen" }}
      </v-btn>
    </div>
  </v-card-text>
</template>

<script setup>
import { inject } from "vue";

// Inject benötigte Daten und Funktionen
const globalModelle = inject("globalModelle");
const displayedModelle = inject("displayedModelle");
const showAllModelle = inject("showAllModelle");
const toggleShowAllModelle = inject("toggleShowAllModelle");
const truncateText = inject("truncateText");
const editModel = inject("editModel");
</script>

<style scoped>
.card-content {
  padding: 16px;
}

.model-list {
  max-height: 350px;
  overflow-y: auto;
  background: rgba(30, 30, 40, 0.3);
  border-radius: 8px;
  padding: 4px;
  border: 0px solid rgba(255, 255, 255, 0.05);
}

.model-item {
  cursor: pointer;
  margin-bottom: 8px;
  border-radius: 8px;
  transition: all 0.2s ease;
  padding: 10px 14px;
  background: rgba(30, 30, 40, 0.5);
  backdrop-filter: blur(5px);
  -webkit-backdrop-filter: blur(5px);
  border-left: 3px solid transparent;
}

.model-item:hover {
  background-color: rgba(35, 35, 45, 0.6);
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  border-left: 3px solid rgba(76, 175, 80, 0.7);
}

.v-list-item-title {
  font-size: 0.95rem;
  font-weight: 500;
  color: rgba(255, 255, 255, 0.9);
  margin-bottom: 2px;
}

.model-cups {
  font-size: 0.85rem;
  opacity: 0.8;
  font-weight: normal;
  margin-left: 3px;
}

.model-details {
  display: flex;
  flex-wrap: wrap;
  margin-bottom: 2px;
}

.temp-chip,
.feature-chip {
  font-size: 0.7rem !important;
  height: 22px !important;
}

.v-list-item-subtitle {
  font-size: 0.8rem;
  color: rgba(255, 255, 255, 0.6);
  white-space: normal;
  display: -webkit-box;
  line-clamp: 2;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
  text-overflow: ellipsis;
}

.show-more-button {
  font-size: 0.85rem;
  opacity: 0.9;
  transition: all 0.3s ease;
}

.show-more-button:hover {
  opacity: 1;
  transform: translateY(-1px);
}

.show-more-button .v-icon {
  transition: transform 0.3s ease;
}

.show-more-button:hover .v-icon {
  transform: translateY(-2px);
}
</style>
